import type { StructuredDiffPayload } from "~/components/StructuredDiffViewer/StructuredDiffViewer";
import { getToolDisplayName } from "~/utils/stepNameMappings";
import type { ChatMessage as ChatMessageType } from "../types";

/**
 * ストリーミングチャンク / ワークフロー監視レコードを処理する統一ハンドラーを作成
 *
 * streamWorkflow（新規実行）と watchWorkflow（resume）の両方で使用される。
 * - setStreamingContent: streamWorkflow 側でエラーメッセージ表示に使用
 * - setStreamingMessageId: watchWorkflow 側で完了/エラー時にストリーミング状態を解除
 */
export const createChunkHandler =
  (
    targetMessageId: string,
    setMessages: React.Dispatch<React.SetStateAction<ChatMessageType[]>>,
    options?: {
      setStreamingContent?: React.Dispatch<React.SetStateAction<string>>;
      setStreamingMessageId?: React.Dispatch<React.SetStateAction<string | null>>;
      setStructuredDiff?: React.Dispatch<React.SetStateAction<StructuredDiffPayload | null>>;
    },
  ) =>
  (record: any) => {
    if (!record || !targetMessageId) return;

    if (record.type === "tool-call-display") {
      const toolName = record.toolName || "tool";
      const displayName = getToolDisplayName(toolName);
      if (!displayName) return;
      const toolCallId = record.toolCallId || `tool-${toolName}-${Date.now()}`;
      setMessages((prev) =>
        prev
          .map((msg) => {
            if (!msg || msg.id !== targetMessageId) return msg;
            const existing = (msg as any).toolCalls || [];
            if (existing.some((tc: any) => tc.toolCallId === toolCallId)) return msg;
            return {
              ...msg,
              toolCalls: [
                ...existing,
                { toolName, toolCallId, state: "running" as const, displayName },
              ],
            } as any;
          })
          .filter(Boolean),
      );
    } else if (record.type === "tool-result") {
      const toolResult = record.toolResult;
      const toolName = toolResult?.toolName || toolResult?.name || "tool";
      const displayName = getToolDisplayName(toolName);
      if (!displayName) return;
      const toolCallId = toolResult?.toolCallId || toolResult?.id || `${toolName}-${Date.now()}`;
      const structuredDiff =
        toolName === "updateDocumentTool" &&
        toolResult &&
        typeof toolResult === "object" &&
        "result" in toolResult &&
        toolResult.result &&
        typeof toolResult.result === "object" &&
        "structuredDiff" in toolResult.result
          ? (toolResult.result.structuredDiff as StructuredDiffPayload | null | undefined)
          : null;
      if (structuredDiff) {
        options?.setStructuredDiff?.(structuredDiff);
      }
      setMessages((prev) =>
        prev
          .map((msg) => {
            if (!msg || msg.id !== targetMessageId) return msg;
            const existing = (msg as any).toolCalls || [];
            const updated = existing.some((tc: any) => tc.toolCallId === toolCallId)
              ? existing.map((tc: any) =>
                  tc.toolCallId === toolCallId
                    ? { ...tc, state: "success" as const, result: toolResult }
                    : tc,
                )
              : [
                  ...existing,
                  {
                    toolName,
                    toolCallId,
                    state: "success" as const,
                    result: toolResult,
                    displayName,
                  },
                ];
            return {
              ...msg,
              toolCalls: updated,
              ...(structuredDiff ? { structuredDiff } : {}),
            } as any;
          })
          .filter(Boolean),
      );
    } else if (record.type === "error") {
      const friendly =
        typeof record.message === "string" && record.message
          ? record.message
          : "エラーが発生しました";
      const finalMessage = friendly.startsWith("エラー") ? friendly : `エラー: ${friendly}`;
      options?.setStreamingContent?.(finalMessage);
      setMessages((prev) =>
        prev
          .map((msg) => {
            if (!msg || msg.id !== targetMessageId) return msg;
            const existing = (msg as any).toolCalls || [];
            const updated =
              existing.length > 0
                ? [
                    ...existing.slice(0, -1),
                    { ...existing[existing.length - 1], state: "error" as const },
                  ]
                : existing;
            const { updatedContent: _u, structuredDiff: _s, ...rest } = msg as any;
            return { ...rest, content: finalMessage, toolCalls: updated, isError: true } as any;
          })
          .filter(Boolean),
      );
      options?.setStreamingMessageId?.(null);
    } else if (record.type === "step-start") {
      // record.stepName は mapStreamChunkToRecord で既に表示名に変換済み
      const stepName = record.stepName;
      if (stepName) {
        setMessages((prev) =>
          prev
            .map((msg) => {
              if (!msg || msg.id !== targetMessageId) return msg;
              const existing = (msg as any).toolCalls || [];
              if (existing.some((tc: any) => tc.toolName === stepName)) return msg;
              return {
                ...msg,
                toolCalls: [
                  ...existing,
                  {
                    toolName: stepName,
                    toolCallId: `step-${stepName}-${Date.now()}`,
                    state: "running" as const,
                    displayName: stepName,
                  },
                ],
              } as any;
            })
            .filter(Boolean),
        );
      }
    } else if (record.type === "step-finish") {
      // record.stepName は mapStreamChunkToRecord で既に表示名に変換済み
      const stepName = record.stepName;
      if (stepName) {
        setMessages((prev) =>
          prev
            .map((msg) => {
              if (!msg || msg.id !== targetMessageId) return msg;
              const existing = (msg as any).toolCalls || [];
              const updated = existing.map((tc: any) => {
                if (tc.toolName === stepName && tc.state === "running") {
                  return { ...tc, state: "success" as const };
                }
                return tc;
              });
              if (!updated.some((tc: any) => tc.toolName === stepName)) {
                updated.push({
                  toolName: stepName,
                  toolCallId: `step-${stepName}-${Date.now()}`,
                  state: "success" as const,
                  displayName: stepName,
                });
              }
              return { ...msg, toolCalls: updated } as any;
            })
            .filter(Boolean),
        );
      }
    } else if (record.type === "workflow-finish") {
      const result = record.result ?? {};
      setMessages((prev) =>
        prev
          .map((msg) => {
            if (!msg || msg.id !== targetMessageId) return msg;
            const nextContent =
              typeof result?.message === "string" && result.message
                ? result.message
                : (record.message ?? msg.content ?? "ワークフローが完了しました。");
            const nextUpdated =
              typeof result?.updatedContent === "string" && result.updatedContent
                ? result.updatedContent
                : typeof record.updatedContent === "string"
                  ? record.updatedContent
                  : (msg as any).updatedContent;
            const nextSourceUrls = Array.isArray(result?.sourceUrls)
              ? (result.sourceUrls as Array<{ title: string; url: string }>)
              : undefined;
            const nextStructuredDiff =
              result &&
              typeof result === "object" &&
              "structuredDiff" in result &&
              result.structuredDiff
                ? (result.structuredDiff as StructuredDiffPayload)
                : (record.structuredDiff ?? (msg as any).structuredDiff ?? null);
            return {
              ...msg,
              content: nextContent,
              updatedContent: nextUpdated,
              structuredDiff: nextStructuredDiff,
              ...(nextSourceUrls ? { sourceUrls: nextSourceUrls } : {}),
            };
          })
          .filter(Boolean),
      );
      options?.setStreamingMessageId?.(null);
    }
  };

/**
 * ストリーミング完了後の後処理を実行
 * @param aiMessageId 対象のAIメッセージID
 * @param streamingContent ストリーミングコンテンツ
 * @param updatedContent 更新されたドキュメントコンテンツ
 * @param setMessages メッセージ更新用のステート更新関数
 */
export const finalizeMessage = (
  aiMessageId: string,
  streamingContent: string,
  updatedContent: string | null,
  structuredDiff: StructuredDiffPayload | null,
  setMessages: React.Dispatch<React.SetStateAction<ChatMessageType[]>>,
) => {
  // 本文が空の場合のみstreamingContentを適用
  setMessages((prev) =>
    prev
      .map((msg) => {
        if (msg?.id !== aiMessageId) return msg;
        if (msg.content && String(msg.content).trim().length > 0) return msg;
        let finalContent: any = streamingContent || msg.content;
        if (typeof finalContent === "object" && finalContent !== null) {
          finalContent = (finalContent as any).message ?? JSON.stringify(finalContent, null, 2);
        }
        return { ...msg, content: finalContent };
      })
      .filter(Boolean),
  );

  // 構造化データを処理
  if (updatedContent) {
    setMessages((prev) =>
      prev
        .map((msg) => {
          if (msg?.id !== aiMessageId) return msg;
          return {
            ...msg,
            updatedContent: updatedContent,
            ...(structuredDiff ? { structuredDiff } : {}),
          };
        })
        .filter(Boolean),
    );
  }
};
